<template>
  <div
    class="wt2-gallery-asset"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
    @click="printAsset"
    :class="{
      '-selected': isSelected,
      '-menu-active': menuActive,
      '-disabled': isDisabled,
      '-is-fading': isFading,
    }"
  >
    <template v-if="isUnfinishedAsset">
      <component
        v-if="uploadStatusSpecific"
        :is="uploadStatusSpecific"
        :asset="asset"
        @remove-asset="
          deleteAsset(asset);
          ABORT_GALLERY_UPLOAD_ASSET(asset);
        "
      />
    </template>
    <template v-else>
      <wt-asset-actions
        :asset="asset"
        :selected="isSelected"
        @menuActive="setMenuActive($event)"
        @preview-asset="previewAsset"
        @add="selectAsset(asset)"
        @remove="ASSET_UNSELECTED(asset)"
        @delete="deleteAsset"
        @edit="editAsset"
      />
      <transition name="wt2-fade-fast">
        <wt-asset-favorite
          @favorite="favoriteAsset"
          :isFavorite="isFavorite"
          :asset="asset"
          v-show="
            (isFavorite && !isFavoriteTab) || hover || isFavoriteInProgress
          "
        />
      </transition>

      <component
        :is="assetTypeSpecific"
        :selected="isSelected"
        :asset="asset"
        :searchContextId="searchContextId"
        :key="asset._id"
        @preview-asset="previewAsset"
        @add="ASSET_SELECTED({ asset })"
      >
        <div slot="checkmark" class="-checkmark-box" v-show="isSelected">
          <i class="-checkmark wticon wticon-v_v8" />
        </div>
      </component>
    </template>
  </div>
</template>

<script>
import app from 'app';
import wtUploadAsset from './states/wtUploadAsset.vue';
import wtProcessingAsset from './states/wtProcessingAsset.vue';
import wtFailedAsset from './states/wtFailedAsset.vue';
import AssetActions from './wtAssetActions.vue';
import ImageAsset from './types/wtImageAsset.vue';
import VideoAsset from './types/wtVideoAsset.vue';
import InstagramAsset from './types/wtInstagramAsset.vue';
import TwitterAsset from './types/wtTwitterAsset.vue';
import FacebookAsset from './types/wtFacebookAsset.vue';
import GifAsset from './types/wtGifAsset.vue';
import wtMapAsset from './types/wtMapAsset.vue';
import AssetMixins from './mixins/AssetMixins.vue';
import AssetFavorite from 'components/assets/mixins/AssetFavorite.vue';

export default {
  mixins: [AssetMixins],
  components: {
    wtUploadAsset,
    wtProcessingAsset,
    wtFailedAsset,
    'wt-asset-actions': AssetActions,
    'wt-image-asset': ImageAsset,
    'wt-video-asset': VideoAsset,
    'wt-gif-asset': GifAsset,
    'wt-instagram-asset': InstagramAsset,
    'wt-twitter-asset': TwitterAsset,
    'wt-facebook-asset': FacebookAsset,
    'wt-map-asset': wtMapAsset,
    'wt-asset-favorite': AssetFavorite,
  },

  props: {
    asset: { type: Object, required: true },
    uploading: { type: Boolean, required: false, default: false },
    observer: { type: IntersectionObserver, required: false },
  },

  data() {
    return {
      menuActive: false,
      hover: false,
      isFading: false,
    };
  },

  computed: Object.assign(
    {
      isFavorite() {
        return app.assetType.isFavorite(this.asset);
      },
      isFavoriteInProgress() {
        return !!this.isAssetProcessingFavorite(this.asset);
      },
      isFavoriteTab() {
        return this.searchContextId === app.PROVIDERS.FAVORITES.id;
      },
      isUnfinishedAsset() {
        return (
          this.asset.metadata.uploadStatus &&
          this.asset.metadata.uploadStatus !== app.uploadStatus.DONE &&
          !this.isSelected &&
          this.uploadStatusSpecific
        );
      },

      uploadStatusSpecific() {
        const status = this.asset.metadata.uploadStatus;
        if (status) {
          return 'wt-' + _.kebabCase(status) + '-asset';
        } else {
          return null;
        }
      },

      assetTypeSpecific() {
        if (app.assetType.isOnlyIMAGE(this.asset)) {
          return 'wt-image-asset';
        }
        if (app.assetType.isFRAGMENT(this.asset)) {
          return 'wt-fragment-asset';
        }
        if (app.assetType.isVIDEO(this.asset)) {
          return 'wt-video-asset';
        }
        if (app.assetType.isGIF(this.asset)) {
          return 'wt-gif-asset';
        }
        if (app.assetType.isTWITTER(this.asset)) {
          return 'wt-twitter-asset';
        }
        if (app.assetType.isINSTAGRAM(this.asset)) {
          return 'wt-instagram-asset';
        }
        if (app.assetType.isFACEBOOK(this.asset)) {
          return 'wt-facebook-asset';
        }
        if (app.assetType.isMAP(this.asset)) {
          return 'wt-map-asset';
        }
        return 'wt-image-asset';
      },

      isSelected() {
        return app.namespace.Controller.AssetController.isAssetSelected(
          this.asset
        );
      },

      isDisabled() {
        return !!this.asset._deleteInProgress;
      },
    },
    app.Vuex.mapGetters(['isAssetProcessingFavorite']),
    app.Vuex.mapState({
      searchContextId: (state) => state.assetGallery.searchContextId,
    })
  ),

  beforeDestroy() {
    this.observer && this.observer.unobserve(this.$el);
  },

  mounted() {
    this.observer && this.observer.observe(this.$el);
  },

  watch: {
    'asset.metadata.uploadStatus': {
      handler(newStatus) {
        if (this.uploading && newStatus === app.uploadStatus.DONE) {
          this.$emit('upload-done', this.asset);
        }
      },
      immediate: true,
    },
    // 'observer': {
    //   handler() {
    //     this.observer && this.observer.observe(this.$el);
    //   },
    //   immediate: true
    // }
  },

  methods: Object.assign(
    {
      deleteAsset() {
        app.namespace.Controller.AssetController.deletePrivatelyUploadedAsset(
          this.asset
        );
      },

      selectAsset(asset) {
        this.ASSET_SELECTED({ asset });
      },

      setMenuActive(isActive) {
        this.menuActive = isActive;
      },

      printAsset(e) {
        if (e && e.metaKey) {
          this.$log(this.asset);
        }
      },

      previewAsset() {
        if (this.isPreparing || this.isPreparingFailed || this.isDisabled) {
          return;
        }
        this.$emit('preview-asset', this.asset, {
          isSelected: this.isSelected,
        });
      },

      getVideoDuration() {
        const { providerLowResAssetUrl: lowRes } = this.asset.metadata;
        const { providerHiResAssetUrl: hiRes } = this.asset.metadata;
        const videoUrl = lowRes || hiRes;
        // checking duration only when SETTING social video as favorite
        if (
          app.assetType.isSOCIALVIDEO(this.asset) &&
          !this.asset.metadata.favoriteOwner
        ) {
          return new Promise((resolve) => {
            const url = app.assetType.isINSTAGRAM(this.asset)
              ? this.mixinProxySrc(videoUrl)
              : this.mixinSrc(videoUrl);
            const video = document.createElement('video');
            video.setAttribute('src', url);
            video.addEventListener('loadedmetadata', () => {
              resolve(video.duration);
            });
          });
        } else {
          return Promise.resolve(0);
        }
      },

      editAsset() {
        if (this.isPreparing || this.isPreparingFailed) {
          return;
        }
        this.mixinShowViewInModal({
          type: 'normal',
          component: 'wt-edit-asset-details',
          componentPropsData: {
            asset: this.asset,
          },
          closeOnEscape: true,
          closeOnClickOut: false,
        }).$on('close', (e) => {
          if (e && e.metadata) {
            const updateOptions = {
              metadata: e.metadata,
              assetUUID: e.assetUUID,
            };
            this.GALLERY_ASSET_METADATA_EDITED(updateOptions);
            this.SELECTED_ASSET_METADATA_EDITED(updateOptions);
            app.trigger('app.asset.edited', updateOptions);
          }
        });
      },
      async toggleFavoriteAsset() {
        const duration = await this.getVideoDuration();
        this.TOGGLE_FAVORITE_ASSET({ asset: this.asset, duration });
      },
      favoriteAsset() {
        if (this.isFading) {
          this.isFading = false;
          return;
        }
        if (
          this.asset.metadata.favoriteOwner &&
          this.isFavoriteTab &&
          !this.isFavoriteInProgress
        ) {
          _.delay(async () => {
            await this.toggleFavoriteAsset();
            this.isFading = true;
          }, 550);
        } else {
          this.toggleFavoriteAsset();
        }
      },
    },
    app.Vuex.mapActions([
      'TOGGLE_FAVORITE_ASSET',
      'ASSET_SELECTED',
      'ASSET_UNSELECTED',
      'ASSET_DELETE',
      'SELECTED_ASSET_METADATA_EDITED',
      'GALLERY_ASSET_METADATA_EDITED',
      'ABORT_GALLERY_UPLOAD_ASSET',
    ])
  ),
};
</script>

<style lang="scss">
.wt2-gallery-asset {
  height: 100%;
  .wt-upload-asset,
  .wt-processing-asset {
    cursor: default;
    transform: translateY(56%);
  }
  .wt-failed-asset {
    cursor: default;
    height: 100%;
  }
}
</style>
